/**
 * A lista de clientes (D24) — o caminho até a ficha da D03.
 *
 * ── O que a linha traz, e o que ela recusa a trazer ───────────────────────
 *
 * O suficiente para DECIDIR qual cliente abrir: nome, contato mascarado,
 * quando foi a última interação e quantas oportunidades estão abertas. É a
 * lista de campos que a task pede, e ela é curta de propósito.
 *
 * O telefone vem **mascarado**, e o e-mail também. A A06 registrou que a lista
 * é onde o vazamento em volume acontece: uma tela com cem telefones completos
 * é uma tela que alguém fotografa. A ficha mostra um de cada vez, e ali a
 * leitura fica registrada.
 *
 * `tem_documento` é booleano pelo mesmo motivo da C10: dizer que existe
 * documento ajuda a decidir; dizer qual é não ajuda em nada nesta tela.
 *
 * ── Por que a linha cresceu ───────────────────────────────────────────────
 *
 * Porque a tela deixou de ser agenda telefônica e virou FILA DE TRABALHO: a
 * pergunta que ela responde não é "quais clientes existem", é "para quem eu
 * ligo agora". Nome e contato não respondem isso — quem responde é responsável,
 * recência e valor em jogo, que é o que entrou aqui.
 *
 * Cada campo novo tem um recorte que depende dele, e nenhum é enfeite:
 * `responsavel` sustenta "meus clientes" e "sem responsável", `negocio`
 * sustenta "com negócio aberto" e a ordem por valor, e `etiquetas` é o assunto
 * — a única coisa da linha que diz DO QUE se trata.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';
import { InstanteSchema } from './valores.js';

/**
 * O mesmo par que os blocos do 360 usam.
 *
 * Repetido aqui, e não importado de lá: o 360 o mantém privado, e exportá-lo
 * de uma tela para outra criaria uma dependência entre contratos que não têm
 * nada a ver um com o outro. Duas linhas iguais custam menos que esse laço.
 */
const FrescorSchema = z.object({
  stale: z.boolean(),
  projetado_em: InstanteSchema.nullable(),
});

export const ClienteDaListaSchema = z
  .object({
    /** Canônico (§5.1) — é por ele que a ficha abre. */
    id: IdSchema,
    nome_exibicao: z.string().nullable(),
    /** Mascarado: `••••7766`. A ficha revela. */
    telefone: z.string().nullable(),
    /** Mascarado: `m•••@exemplo.com`. */
    email: z.string().nullable(),
    tem_documento: z.boolean(),
    /** A mensagem mais recente de qualquer conversa dele. `null` = nunca falou. */
    ultima_interacao_em: InstanteSchema.nullable(),
    /**
     * Por onde foi esse contato: o canal da caixa da conversa mais recente
     * (`whatsapp_cloud`, `instagram`…). A tela escreve o nome dele.
     */
    ultima_interacao_canal: z.string().nullable().optional(),
    oportunidades_abertas: z.number().int(),
    /**
     * Quem atende, pela conversa mais recente que tem atendente.
     *
     * Cliente NÃO TEM DONO no modelo — quem tem responsável é a oportunidade e
     * a conversa (§5.1). Entre as duas, a conversa é a que traz o NOME junto
     * (`read.conversations.atendente_nome`, migração 038); a oportunidade traz
     * só o id da engine, e uma coluna "Responsável" com um UUID dentro não
     * responde a pergunta que ela existe para responder.
     *
     * `null` é PENDÊNCIA, e a tela a escreve em vermelho: cliente sem ninguém
     * atrás é o que se perde, não uma célula vazia.
     */
    responsavel: z.object({ id_externo: z.string(), nome: z.string().nullable() }).nullable(),
    /**
     * O assunto, pelos títulos — a união das etiquetas das conversas dele.
     *
     * Sem a COR: ela mora no catálogo da revenda (`catalogoDeEtiquetas`), e
     * copiá-la para cada linha faria trocar a cor de uma etiqueta exigir
     * reprojetar toda conversa que a tem.
     */
    etiquetas: z.array(z.string()),
    /**
     * O MAIOR negócio aberto — etapa, valor e moeda da mesma oportunidade.
     *
     * Uma só, e não a soma: somar exigiria que todas estivessem na mesma moeda,
     * e a etapa de uma soma não existe. Quantas mais existem já está em
     * `oportunidades_abertas`, e é o que a tela usa para dizer que há outras.
     *
     * `etapa` vem CRUA (`MEETING`), como no resto do produto: traduzir é da
     * tela, e a revenda que renomeou a etapa já escolheu o nome dela.
     */
    negocio: z
      .object({
        etapa: z.string().nullable(),
        /** O nome que se lê ("Novo · Agendamento"), pronto; sem ele, a tela mostra a `etapa`. */
        etapa_nome: z.string().nullable().optional(),
        /** A cor da etapa no funil (`green`, `blue`…), a do chip; `null` sem etapa no funil. */
        etapa_cor: z.string().nullable().optional(),
        valor_micros: z.number().int().nullable(),
        moeda: z.string().nullable(),
      })
      .nullable(),
    /**
     * A conversa mais recente, para a ação da linha abrir o atendimento.
     *
     * Canônico (§5.1), nunca o id do Chatwoot. `null` = cliente que nunca
     * falou por canal nenhum, e aí a ação não tem para onde levar.
     */
    conversa_id: IdSchema.nullable(),
  })
  .describe('Cliente na lista, com contato mascarado');

/**
 * Os recortes de trabalho da lista.
 *
 * Cada um responde a uma pergunta que alguém faz de manhã, e é por isso que
 * cidade, origem e faixa de valor NÃO estão aqui: aquilo é busca avançada, que
 * é outra tela. Aqui cada pílula é uma fila.
 *
 *   esfriando         tem negócio aberto e ninguém fala há trinta dias — é
 *                     dinheiro saindo pela porta, e é por isso que a lista
 *                     abre nele
 *   meus              tem conversa atendida por mim
 *   sem_responsavel   nenhuma conversa tem atendente: a fila de quem ainda não
 *                     tem dono
 *   com_negocio       tem oportunidade aberta
 *   todos             a base inteira, dentro da carteira
 */
/**
 * Quantos dias sem contato tornam um cliente "esfriando".
 *
 * Vive no contrato porque os dois lados precisam do MESMO número: a API monta o
 * recorte com ele, e a tela pinta a recência de âmbar com ele. Duas constantes
 * divergiriam no primeiro ajuste, e a lista mostraria em âmbar um cliente que o
 * filtro não traz — o defeito mais confuso possível, porque cada metade está
 * certa sozinha.
 */
export const DIAS_PARA_ESFRIAR = 30;

export const RecorteDeClientesSchema = z.enum([
  'esfriando',
  'meus',
  'sem_responsavel',
  'com_negocio',
  'todos',
]);

/**
 * Por qual coluna a lista se ordena.
 *
 * Três, e não seis: são as que têm ordem própria. Etiqueta e responsável
 * ordenariam por texto, que não diz nada numa fila — e cada ordem nova é uma
 * chave a mais no cursor, que é onde a paginação quebra em silêncio.
 */
export const OrdemDeClientesSchema = z.enum(['ultimo_contato', 'nome', 'valor']);

/** As contagens de cada recorte, para a pílula dizer onde há trabalho. */
export const ContagensDeClientesSchema = z.object({
  esfriando: z.number().int(),
  meus: z.number().int(),
  sem_responsavel: z.number().int(),
  com_negocio: z.number().int(),
  todos: z.number().int(),
});

export const PaginaDeClientesSchema = z.object({
  itens: z.array(ClienteDaListaSchema),
  cursor_proximo: z.string().nullable(),
  tem_mais: z.boolean(),
  /**
   * O total DENTRO do escopo, e o nome do campo diz isso.
   *
   * Contar antes de aplicar a carteira daria "1.240 clientes" a quem enxerga
   * oitenta — a Armadilha que a D24 nomeia. O nome do campo existe para que
   * ninguém o exiba como "total de clientes da revenda".
   */
  total_no_escopo: z.number().int(),
  /**
   * Quantos clientes cada recorte tem, com a BUSCA aplicada e o recorte não.
   *
   * É o que evita o clique de descoberta: dá para ver onde há trabalho antes
   * de filtrar. E é o que dá o "de 1.284" ao lado do título — `todos` é o
   * tamanho da base dentro da carteira, e `total_no_escopo` é o do recorte
   * ligado agora.
   */
  contagens: ContagensDeClientesSchema,
  frescor: FrescorSchema,
});

/**
 * Uma linha do arquivo exportado — e a diferença para a linha da lista.
 *
 * ── Aqui o contato vem INTEIRO, e é o ponto ───────────────────────────────
 *
 * A lista mascara telefone e e-mail porque é onde o vazamento em volume
 * acontece (A06): uma tela com cem telefones completos é uma tela que alguém
 * fotografa. A exportação é esse vazamento por definição — é um arquivo com a
 * base inteira —, e mascará-lo produziria uma planilha da qual não se pode
 * ligar para ninguém, ou seja, nenhuma exportação.
 *
 * O que sustenta a diferença não é o formato: é o REGISTRO. Toda linha deste
 * arquivo vira uma entrada em `core.data_access_log` (§13.1), na mesma
 * transação da leitura — e só o `admin_tenant` pode pedir (§13.2). Um acesso
 * que aconteceu e que ninguém consegue provar é o que a §13.1 existe para
 * tornar impossível.
 *
 * ── O documento NÃO vai, nem cifrado ──────────────────────────────────────
 *
 * Ele é guardado cifrado e ver de novo exige o step-up da B17, com
 * justificativa. Uma planilha o entregaria em claro para quem só tem a senha —
 * seria a porta dos fundos da B17 inteira. O arquivo diz apenas se existe.
 */
export const ClienteExportadoSchema = z.object({
  id: IdSchema,
  nome: z.string().nullable(),
  /** INTEIRO, em E.164 — ver o cabeçalho. */
  telefone: z.string().nullable(),
  /** INTEIRO — ver o cabeçalho. */
  email: z.string().nullable(),
  tem_documento: z.boolean(),
  responsavel: z.string().nullable(),
  etiquetas: z.array(z.string()),
  etapa: z.string().nullable(),
  /** O nome que se lê ("Novo · Agendamento"): é o que vai para a coluna da planilha. */
  etapa_nome: z.string().nullable().optional(),
  valor_micros: z.number().int().nullable(),
  moeda: z.string().nullable(),
  oportunidades_abertas: z.number().int(),
  ultima_interacao_em: InstanteSchema.nullable(),
  /**
   * Os campos personalizados (#568), já por extenso, pelo id do campo: a opção
   * pelo nome, a data como se escreve. O vazio não aparece.
   */
  campos: z.record(z.string(), z.string()),
});

export const ExportacaoDeClientesSchema = z.object({
  itens: z.array(ClienteExportadoSchema),
  /** As colunas dos campos personalizados, na ordem do catálogo, com o nome que a organização deu. */
  campos: z.array(z.object({ id: IdSchema, nome: z.string() })),
  /**
   * Quantos ficaram de fora por causa do teto.
   *
   * Zero na esmagadora maioria das vezes. Quando não é, a tela precisa dizer —
   * um arquivo silenciosamente truncado é pior que uma recusa, porque quem o
   * recebe acha que tem a base inteira.
   */
  truncada_em: z.number().int().nullable(),
  gerada_em: InstanteSchema,
});

/**
 * O cartão de um contato, para compartilhar na conversa (#595).
 *
 * A lista devolve o telefone MASCARADO, de propósito: ela mostra muitos
 * clientes de uma vez. Este é o telefone inteiro de UM, o escolhido, e cada
 * leitura fica em `core.data_access_log` com finalidade própria — é o rastro de
 * quem leu o telefone de quem. Ler não é enviar: o registro não diz a conversa
 * de destino.
 */
export const CartaoDeContatoSchema = z.object({
  id: IdSchema,
  nome: z.string().nullable(),
  /** O cargo que o CRM guarda para a pessoa. `null` quando não há. */
  cargo: z.string().nullable(),
  /** E.164, com o `+`. `null` = sem telefone: não há o que compartilhar. */
  telefone: z.string().nullable(),
});

export type CartaoDeContato = z.infer<typeof CartaoDeContatoSchema>;
export type ClienteDaLista = z.infer<typeof ClienteDaListaSchema>;
export type ClienteExportado = z.infer<typeof ClienteExportadoSchema>;
export type ExportacaoDeClientes = z.infer<typeof ExportacaoDeClientesSchema>;
export type RecorteDeClientes = z.infer<typeof RecorteDeClientesSchema>;
export type OrdemDeClientes = z.infer<typeof OrdemDeClientesSchema>;
export type ContagensDeClientes = z.infer<typeof ContagensDeClientesSchema>;
export type PaginaDeClientes = z.infer<typeof PaginaDeClientesSchema>;
